/**
 * The DEM provider contract (SPEC §3.1).
 *
 * Every provider returns the same normalised `HeightGrid`, so adding a source
 * must never require touching the solver. Anything provider-specific — tiling,
 * auth, GeoTIFF decoding, coordinate systems — stops here.
 */

import type { HeightGrid } from '../physics/types';
import type { BBox, MetricDomain } from './projection';

export type { HeightGrid };
export type { BBox };

export type ProviderId =
  | 'swisstopo'
  | 'ign-rge-alti'
  | 'usgs-3dep'
  | 'copernicus-glo30'
  | 'terrarium';

export interface ProviderInfo {
  id: ProviderId;
  label: string;
  /** nominal native resolution, metres */
  nativeResolution: number;
  /** attribution string, surfaced in the UI (SPEC §11) */
  attribution: string;
  /** rough region of validity, for the resolver's tier ordering */
  covers: (bbox: BBox) => boolean;
}

export interface DemRequest {
  bbox: BBox;
  /** working grid dimension; must be even for the multigrid hierarchy */
  gridSize: number;
  /** abort in-flight network work when the user pans away */
  signal?: AbortSignal;
}

export interface DemResult {
  grid: HeightGrid;
  domain: MetricDomain;
  provider: ProviderId;
  attribution: string;
  /** true when this came from IndexedDB rather than the network */
  cached: boolean;
}

export interface DemProvider {
  info: ProviderInfo;
  load(req: DemRequest, domain: MetricDomain): Promise<HeightGrid>;
}

/** Thrown when a provider has no data for the requested area, so we fall through. */
export class NoCoverageError extends Error {
  constructor(provider: string) {
    super(`No coverage from ${provider}`);
    this.name = 'NoCoverageError';
  }
}

const inBox = (b: BBox, w: number, s: number, e: number, n: number) =>
  b[0] < e && b[2] > w && b[1] < n && b[3] > s;

export const REGIONS = {
  switzerland: (b: BBox) => inBox(b, 5.9, 45.8, 10.5, 47.9),
  france: (b: BBox) => inBox(b, -5.2, 41.3, 9.6, 51.1),
  usa: (b: BBox) => inBox(b, -125, 24, -66.9, 49.4) || inBox(b, -168, 54, -129, 72),
  global: () => true,
};
